import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import type {
  BugSeverity,
  EmployeeSummary,
  PageResponse,
  WorkItemDetail,
  WorkItemSummary,
} from "../../shared/types/api";
import { SEVERITY_LABELS } from "./workItemVocabulary";

const today = () => new Date().toISOString().slice(0, 10);

const SEVERITIES: BugSeverity[] = ["BLOCKER", "CRITICAL", "MAJOR", "MINOR", "TRIVIAL"];

/**
 * Raises an impediment (§86).
 *
 * <p>An owner and an expected date are asked for, and both are the point. A blocker with neither
 * is a complaint: nobody is chasing it, nothing marks it as overdue, and it sits there quietly
 * accumulating the age that eventually explains a missed deadline.
 *
 * <p>The blocking work item is optional. Most of the impediments that actually cost time are not
 * work items at all — an access request, a hardware order, an answer nobody has given.
 */
export function BlockerDrawer({
  item,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [blockingId, setBlockingId] = useState("");
  const [severity, setSeverity] = useState<BugSeverity>("MAJOR");
  const [raisedDate, setRaisedDate] = useState(today());
  const [expected, setExpected] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: Boolean(item),
  });

  const siblings = useQuery({
    queryKey: ["work-items", "blocker-candidates", item?.projectId],
    queryFn: () =>
      api.get<PageResponse<WorkItemSummary>>(
        `/api/v1/work-items?projectId=${item?.projectId}&size=200`,
      ),
    enabled: Boolean(item),
  });

  useEffect(() => {
    if (item) {
      setTitle("");
      setDescription("");
      setOwnerId("");
      setBlockingId("");
      setSeverity("MAJOR");
      setRaisedDate(today());
      setExpected("");
      setError(null);
    }
  }, [item]);

  const submit = async () => {
    if (!item) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${item.id}/blockers`, {
        title,
        description: description || null,
        ownerId: ownerId || null,
        blockingWorkItemId: blockingId || null,
        severity,
        raisedDate,
        expectedResolution: expected || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not raise the blocker.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title="Raise a blocker"
      subtitle={item ? `${item.itemKey} — ${item.title}` : undefined}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={title.trim() === ""}
          >
            {saving ? "Raising…" : "Raise blocker"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="What is in the way" required>
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Waiting on VPN access for the test environment"
          />
        </Field>

        <Field label="Detail" hint="Enough that somebody else could pick this up and chase it.">
          <Textarea
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Who is chasing it"
            hint="A blocker with no owner is the one that stays open."
          >
            <Select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
              <option value="">Nobody yet</option>
              {(employees.data?.content ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Severity" required>
            <Select
              value={severity}
              onChange={(event) => setSeverity(event.target.value as BugSeverity)}
            >
              {SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {SEVERITY_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Raised on" required hint="Its age is counted from here.">
            <Input
              type="date"
              value={raisedDate}
              onChange={(event) => setRaisedDate(event.target.value)}
            />
          </Field>
          <Field
            label="Expected to clear"
            hint="Optional. Without one, nothing can mark it overdue."
          >
            <Input
              type="date"
              value={expected}
              min={raisedDate}
              onChange={(event) => setExpected(event.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Blocked by another item"
          hint="Optional — most blockers are not work items at all."
        >
          <Select value={blockingId} onChange={(event) => setBlockingId(event.target.value)}>
            <option value="">Not a work item</option>
            {(siblings.data?.content ?? [])
              .filter((candidate) => candidate.id !== item?.id)
              .map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.itemKey} — {candidate.title}
                </option>
              ))}
          </Select>
        </Field>
      </div>
    </Drawer>
  );
}
